import { ReportWorkbook, ReportEntityType } from 'components/ReportsModule/types';
import KEY_EXPORT_ORGANIZATION_CLASSROOM_REPORT_QUERY from 'graph/reports/key-export-organization-classroom-report-query';
import KEY_EXPORT_ORGANIZATION_REPORT_QUERY from 'graph/reports/key-export-organization-report-query';
import { KeyOrganizationEngagementReportResult } from 'types/__generated__/graphql';
import { DocumentType } from 'types/__generated__/gql';
import { OrganizationType } from 'types/schema-generated-types';
import { mapClassroomWorksheet } from './classroom';

type OrganizationExport = DocumentType<typeof KEY_EXPORT_ORGANIZATION_REPORT_QUERY>;
type OrganizationClassroomExport = DocumentType<
  typeof KEY_EXPORT_ORGANIZATION_CLASSROOM_REPORT_QUERY
>;

const getOrganizationWorksheet = (data: OrganizationExport | OrganizationClassroomExport): any => {
  const {
    organization,
    activeStudents,
    activitiesCompleted,
    averageWeeklyTimeSpent,
    curriculumProgress
  } = data;

  const schools =
    organization?.subOrganizations?.filter(subOrg => subOrg?.type === OrganizationType.SCHOOL) ??
    [];

  return {
    organization: organization?.name,
    organizationType: organization?.type,
    ...(organization?.type === OrganizationType.DISTRICT && { schoolCount: schools.length }),
    classroomCount: organization?.stats.classroomCount,
    educatorCount: organization?.stats.userCount,
    studentCount: activeStudents?.result?.totalStudentCount,
    activeStudents: activeStudents?.result?.activeStudentCount,
    assignedLicenses: organization?.licenseUsage
      ?.map(usage => usage.classroomAssignedCount)
      .reduce((a, b) => a + b, 0),
    overallCurriculumProgress: curriculumProgress?.result?.curriculumProgressPercentage,
    activitiesCompletedTotal: activitiesCompleted?.result?.totalActivitiesCompletedCount,
    ...activitiesCompleted?.result?.curriculumActivitiesCompleted?.reduce((acc, curr) => {
      return {
        ...acc,
        [`activitiesCompleted${curr.productCurriculumCode}`]: curr.activitiesCompletedCount
      };
    }, {} as any),
    averageWeeklyTimeSpentTotal: averageWeeklyTimeSpent?.result?.averageWeeklyMinutesSpent,
    ...averageWeeklyTimeSpent?.result?.curriculumAverageWeeklyTimeSpent?.reduce((acc, curr) => {
      return {
        ...acc,
        [`averageWeeklyTimeSpent${curr.productCurriculumCode}`]: curr.averageWeeklyMinutesSpent
      };
    }, {})
  };
};

const getSubOrganizationWorksheet = (school: KeyOrganizationEngagementReportResult): any => {
  const {
    organization,
    activeStudentCount,
    curriculumProgressPercentage,
    totalStudentCount,
    totalActivitiesCompletedCount,
    curriculumActivitiesCompleted,
    averageWeeklyMinutesSpent,
    curriculumAverageWeeklyTimeSpent
  } = school;

  return {
    organization: organization?.name,
    organizationType: organization?.type,
    classroomCount: organization?.stats.classroomCount,
    educatorCount: organization?.stats.userCount,
    studentCount: totalStudentCount,
    activeStudents: activeStudentCount,
    assignedLicenses: organization?.licenseUsage
      ?.map(usage => usage.classroomAssignedCount)
      .reduce((a, b) => a + b, 0),
    overallCurriculumProgress: curriculumProgressPercentage,
    activitiesCompletedTotal: totalActivitiesCompletedCount,
    ...curriculumActivitiesCompleted?.reduce((acc, curr) => {
      return {
        ...acc,
        [`activitiesCompleted${curr.productCurriculumCode}`]: curr.activitiesCompletedCount
      };
    }, {} as any),
    averageWeeklyTimeSpentTotal: averageWeeklyMinutesSpent,
    ...curriculumAverageWeeklyTimeSpent?.reduce((acc, curr) => {
      return {
        ...acc,
        [`averageWeeklyTimeSpent${curr.productCurriculumCode}`]: curr.averageWeeklyMinutesSpent
      };
    }, {})
  };
};

const getOrganizationClassroomWorkbook = (
  organizationType: ReportEntityType
): ReportWorkbook<OrganizationClassroomExport> => ({
  query: KEY_EXPORT_ORGANIZATION_CLASSROOM_REPORT_QUERY,
  async: true,
  isComplete: (data: OrganizationClassroomExport): boolean => {
    const subqueryResponses = [
      data.activeStudents,
      data.activitiesCompleted,
      data.averageWeeklyTimeSpent,
      data.curriculumProgress,
      data.classrooms
    ];

    return subqueryResponses.every(response => !!response.result);
  },
  getEntityName: (data: OrganizationClassroomExport) => data.organization?.name ?? '',
  worksheets: [
    {
      name: organizationType,
      parseQueryData: (data: OrganizationClassroomExport) => {
        const result = getOrganizationWorksheet(data);
        return [result];
      }
    },
    {
      name: ReportEntityType.CLASSROOM,
      parseQueryData: (data: OrganizationClassroomExport): any[][] => {
        const classrooms: any[] = data?.classrooms?.result ?? [];
        return classrooms.map(mapClassroomWorksheet);
      }
    }
  ]
});

export const ORGANIZATION_WORKBOOK: {
  [ReportEntityType.DISTRICT]: ReportWorkbook<OrganizationExport>;
  [ReportEntityType.SCHOOL]: ReportWorkbook<OrganizationClassroomExport>;
  [ReportEntityType.USER_GROUP]: ReportWorkbook<OrganizationClassroomExport>;
} = {
  [ReportEntityType.DISTRICT]: {
    query: KEY_EXPORT_ORGANIZATION_REPORT_QUERY,
    async: true,
    isComplete: (data): boolean => {
      if (!data) return false;

      const subqueryResponses = [
        data.activeStudents,
        data.activitiesCompleted,
        data.averageWeeklyTimeSpent,
        data.curriculumProgress,
        data.schools
      ];

      return subqueryResponses.every(response => !!response.result);
    },
    getEntityName: data => data.organization?.name ?? '',
    worksheets: [
      {
        name: ReportEntityType.DISTRICT,
        parseQueryData: (data): any[][] => {
          const result = getOrganizationWorksheet(data);
          return [result];
        }
      },
      {
        name: ReportEntityType.SCHOOL,
        parseQueryData: (data): any[][] => {
          const schools: any[] = data?.schools?.result ?? [];
          return schools.map(getSubOrganizationWorksheet);
        }
      }
    ]
  },
  [ReportEntityType.SCHOOL]: getOrganizationClassroomWorkbook(ReportEntityType.SCHOOL),
  [ReportEntityType.USER_GROUP]: getOrganizationClassroomWorkbook(ReportEntityType.USER_GROUP)
};
